import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Activity, 
  BarChart3, 
  Settings, 
  Shield, 
  Cpu, 
  Database,
  Wrench,
  FileText,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Factory,
  Zap,
  Droplets,
  Pickaxe,
  FlaskConical,
  Thermometer,
  Magnet,
  Volume2,
  Waves,
  Battery,
  MapPin,
  CheckCircle
} from 'lucide-react';

const AnimatedCounter = ({ end, duration = 2000, suffix = '' }: { end: number; duration?: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);

  useEffect(() => {
    let startTime: number;
    let animationFrameId: number;
    let flipInterval: number;
    
    // Start with flipping effect
    setIsFlipping(true);
    
    // Random number flipping for first 800ms
    flipInterval = setInterval(() => {
      const randomValue = Math.floor(Math.random() * (end * 1.5));
      setCount(randomValue);
    }, 80);
    
    // Stop flipping and start smooth counting after 800ms
    setTimeout(() => {
      clearInterval(flipInterval);
      setIsFlipping(false);
      
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = (currentTime - startTime) / (duration - 800);

        if (progress < 1) {
          const currentValue = end * progress;
          setCount(Math.floor(currentValue));
          animationFrameId = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };

      animationFrameId = requestAnimationFrame(animate);
    }, 800);

    return () => {
      clearInterval(flipInterval);
      cancelAnimationFrame(animationFrameId);
    };
  }, [end, duration]);

  return (
    <span 
      className={`inline-block transition-all duration-100 ${
        isFlipping ? 'transform scale-110 text-yellow-200' : 'transform scale-100'
      }`}
    >
      {count}{suffix}
    </span>
  );
};

const FAQItem = ({ question, answer, isOpen, onClick }: { question: string; answer: string; isOpen: boolean; onClick: () => void }) => (
  <div className="border border-gray-200 rounded-lg mb-4">
    <button
      onClick={onClick}
      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
    >
      <span className="font-medium text-gray-900">{question}</span>
      {isOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
    </button>
    {isOpen && (
      <div className="px-6 pb-4">
        <p className="text-gray-700 leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

const Home = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const services = [
    { icon: Activity, title: 'Asset Health Monitoring', desc: 'Real-time condition monitoring and health assessment' },
    { icon: BarChart3, title: 'Lifecycle Cost Analysis', desc: 'Comprehensive TCO modeling and optimization' },
    { icon: Settings, title: 'Predictive Maintenance Strategy', desc: 'AI-driven maintenance planning and scheduling' },
    { icon: Shield, title: 'Criticality & Risk Assessments', desc: 'Strategic asset prioritization and risk mitigation' },
    { icon: Cpu, title: 'Digital Twin Development', desc: 'Virtual asset modeling and simulation' },
    { icon: Database, title: 'CMMS Integration', desc: 'Seamless maintenance management system integration' },
    { icon: Wrench, title: 'Failure Mode & RCA Workshops', desc: 'Root cause analysis and failure prevention' },
    { icon: FileText, title: 'Audit & Optimisation Reporting', desc: 'Performance insights and improvement recommendations' }
  ];

  const industries = [
    { 
      name: 'Manufacturing', 
      icon: Factory, 
      proofPoint: '30% downtime reduction in global steel plants'
    },
    { 
      name: 'Energy & Power', 
      icon: Zap, 
      proofPoint: 'Reliability that keeps grids running'
    },
    { 
      name: 'Oil & Gas', 
      icon: Droplets, 
      proofPoint: 'Extended turbine life, $40M saved annually'
    },
    { 
      name: 'Mining', 
      icon: Pickaxe, 
      proofPoint: 'Safer, smarter, more productive operations'
    },
    { 
      name: 'Chemical Processing', 
      icon: FlaskConical, 
      proofPoint: 'Process reliability with full compliance'
    },
    { 
      name: 'Pulp & Paper', 
      icon: FileText, 
      proofPoint: 'Higher output, lower maintenance costs'
    },
  ];

  const whyChoosePoints = [
    'ROI You Can Measure → Higher uptime, lower lifecycle costs.',
    'Engineering + Analytics → Reliability that keeps improving.',
    'Cross-Industry Expertise → Proven success in energy, steel, cement & more.',
    'True Partnership → Dedicated experts by your side, always.',
    'Tech-Agnostic Solutions → We adapt to your systems, not the other way around.',
    'Innovation at Work → Cutting-edge tools for future-ready reliability.'
  ];

  const deviceSpecs = [
    { icon: Thermometer, label: 'Temperature', position: 'top-4 left-8' },
    { icon: Droplets, label: 'Humidity', position: 'top-12 right-8' },
    { icon: Settings, label: 'RPM', position: 'bottom-4 left-4' },
    { icon: Magnet, label: 'Magnetic Flux', position: 'bottom-12 right-12' },
    { icon: Volume2, label: 'Acoustics', position: 'top-20 left-20' },
    { icon: Waves, label: 'Tri-Axial Vibrations', position: 'bottom-8 right-4' },
    { icon: Battery, label: 'Self-Sustaining', position: 'top-8 right-20' },
    { icon: MapPin, label: 'Fits Anywhere', position: 'bottom-20 left-12' }
  ];

  const faqs = [
    {
      question: "What is predictive maintenance and how does it benefit my operations?",
      answer: "Predictive maintenance uses advanced analytics and IoT sensors to predict equipment failures before they occur, reducing unplanned downtime by up to 50% and extending asset life by 20-40%."
    },
    {
      question: "How quickly can I expect to see ROI from asset management solutions?",
      answer: "Most clients see initial benefits within 3-6 months, with full ROI typically achieved within 12-18 months through reduced downtime, optimized maintenance costs, and improved asset performance."
    },
    {
      question: "Do you integrate with existing maintenance management systems?",
      answer: "Yes, we specialize in seamless integration with popular CMMS platforms like SAP PM, Maximo, Maintenance Connection, and others, ensuring your existing workflows remain intact."
    },
    {
      question: "What industries do you serve and what's your experience?",
      answer: "We serve manufacturing, energy, oil & gas, mining, chemical processing, and pulp & paper industries with over 15 years of combined experience and 200+ successful implementations."
    },
    {
      question: "How does your IoT monitoring solution work?",
      answer: "Our IoT devices continuously monitor key parameters like temperature, vibration, and acoustics, transmitting data wirelessly to our analytics platform for real-time insights and predictive alerts."
    },
    {
      question: "Can you help with asset criticality assessment and risk management?",
      answer: "Absolutely. We conduct comprehensive criticality assessments using industry-standard methodologies to identify high-risk assets and develop targeted maintenance strategies to minimize business impact."
    },
    {
      question: "What training and support do you provide?",
      answer: "We offer comprehensive training programs including reliability engineering courses, condition monitoring workshops, and ongoing technical support to ensure your team maximizes the value of our solutions."
    },
    {
      question: "How do digital twins enhance asset management?",
      answer: "Digital twins create virtual replicas of your physical assets, enabling scenario testing, performance optimization, and predictive analytics without impacting actual operations."
    },
    {
      question: "What's included in your asset lifecycle optimization service?",
      answer: "Our service includes performance analysis, cost modeling, maintenance strategy optimization, replacement planning, and continuous improvement recommendations throughout the asset lifecycle."
    },
    {
      question: "Do you provide ongoing support after implementation?",
      answer: "Yes, we provide 24/7 monitoring support, regular performance reviews, system updates, and continuous optimization to ensure sustained benefits from your investment."
    },
    {
      question: "How do you ensure data security and compliance?",
      answer: "We implement enterprise-grade security measures including encrypted data transmission, secure cloud infrastructure, role-based access controls, and compliance with industry standards like ISO 27001."
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#F0F4FE] via-white to-[#E2EEFE] py-20 overflow-hidden">
        {/* Background Image - Hidden on mobile, positioned to right corner */}
        <div 
          className="absolute bg-cover bg-no-repeat hidden md:block"
          style={{ 
            backgroundImage: 'url(/images/AAMS_HeroS_Bg.png)',
            backgroundSize: '60% auto',
            backgroundPosition: 'top right',
            top: '0',
            right: '0',
            width: '100%',
            height: '100%'
          }}
        ></div>
        
        {/* Gradient overlay behind text */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#E2EEFE] to-transparent hidden md:block" style={{ width: '100%' }}></div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 leading-tight mb-6">
                <span className="shine-text animate-shine-text">Smarter Assets.</span><br />
                <span className="shine-text animate-shine-text animation-delay-300">Maximum Performance.</span><br />
                <span className="text-[#233790] shine-text animate-shine-text animation-delay-600">Stronger Futures.</span>
              </h1>
              <p className="text-lg md:text-l text-gray-600 mb-8 leading-relaxed">
              AAMS helps industries unlock asset potential through predictive intelligence, lifecycle optimization, and data-driven strategies. We transform assets into intelligent, future-ready systems with limitless potential.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/services"
                  className="inline-flex items-center px-8 py-3 bg-[#233790] text-white font-medium rounded-lg hover:bg-[#1e2d73] transition-colors"
                >
                  Explore Our Services
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-3 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-[#F0F4FE] transition-colors"
                >
                  Speak to an Asset Advisor
                </Link>
              </div>
            </motion.div>
            {/* ASWARTHA IoT Devices Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <img
                  src="/images/aswartha-iot-devices.jpg"
                  alt="ASWARTHA Industrial IoT Devices"
                  className="w-full h-auto object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Decorative Stripe */}
      <div className="bg-gradient-to-r from-[#233790] to-[#3BA6EA] h-[50px]"></div>

      {/* What We Do */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Your Strategic Partner in Asset Lifecycle Intelligence
            </h2>
            <p className="text-l text-gray-500 max-w-5xl mx-auto leading-relaxed">
              At AAMS, we go beyond asset management. We deliver predictable performance, extended equipment life, and operational certainty. By fusing next gen IoT technology with decades of engineering expertise, we help industries achieve to deliver tangible results, Higher uptime, Lower breakdowns,  Safer Operations. So, you can focus on growth while we ensure your assets perform at their best.
            <p><b>With AAMS, you don’t just maintain your assets, “you maximise their value”.</b></p>
            </p>
            

          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-[#233790] to-[#3BA6EA] rounded-lg flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why AAMS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
             <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Why Choose Aswartha for Asset Management
            </h2>
            <p className="text-l text-gray-500 max-w-5xl mx-auto leading-relaxed">
            Every minute of downtime costs millions. Here’s why companies trust Aswartha to make sure that minute never happens.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChoosePoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-start space-x-4"
              >
                <div className="w-6 h-6 bg-[#233790] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <p className="text-gray-700 leading-relaxed">{point}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">Industries We Serve</h2>
             <p className="text-l text-gray-500 max-w-3xl mx-auto leading-relaxed">
              From refineries to power plants, Aswartha delivers measurable ROI and Zero-breakdown Reliability across every sector we serve.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12 max-w-5xl mx-auto">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flip-card"
              >
                <div className="flip-card-inner">
                  {/* Front Side */}
                  <div className="flip-card-front">
                    <div className="industry-icon-wrapper">
                      <industry.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 text-center leading-tight">
                      {industry.name}
                    </h3>
                    <div className="mt-2 w-12 h-0.5 bg-gradient-to-r from-[#233790] to-[#3BA6EA] mx-auto"></div>
                  </div>
                  
                  {/* Back Side */}
                  <div className="flip-card-back">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                        <industry.icon className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-sm font-medium leading-relaxed">
                        {industry.proofPoint}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center">
            <Link
              to="/industries"
              className="inline-flex items-center px-6 py-3 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-[#F0F4FE] transition-colors"
            >
              View All Industries <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* IoT Device Specs - Redesigned */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-[#F0F4FE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">Advanced IoT Monitoring</h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">Compact. Powerful. Built to Protect Your Assets.</p>
          </motion.div>
          
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 md:p-8">
              {/* Device Image */}
              <motion.div 
                className="flex items-center justify-center p-4 bg-gray-50 rounded-lg"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src="/images/aams-key.png"
                  alt="IoT Device"
                  
                />
              </motion.div>
              
              {/* Specifications List */}
              <motion.div 
                className="space-y-4"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h3 className="text-2xl font-medium text-gray-900 mb-6">Key Specifications</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {deviceSpecs.map((spec, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <spec.icon className="w-4 h-4 text-[#233790]" />
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{spec.label}</h4>
                        <p className="text-sm text-gray-500">
                          {spec.label === 'Temperature' && 'Real-time tracking with ±0.5°C accuracy to prevent overheating failures.'}
                          {spec.label === 'Humidity' && '0-100% RH measurement range'}
                          {spec.label === 'RPM' && 'High-precision rotation speed monitoring for early imbalance detection.'}
                          {spec.label === 'Magnetic Flux' && 'Identifies electrical field variations before they cause downtime.'}
                          {spec.label === 'Acoustics' && 'Ultrasound sensing for early fault detection in critical assets.'}
                          {spec.label === 'Tri-Axial Vibrations' && '3-axis vibration analysis to predict bearing & motor issues'}
                          {spec.label === 'Self-Sustaining' && 'Long-life battery, up to 5 years of uninterrupted operation.'}
                          {spec.label === 'Fits Anywhere' && 'Compact, versatile design for seamless installation.'}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Teaser */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <blockquote className="text-2xl md:text-3xl font-light text-gray-900 mb-8 leading-relaxed">
              "AAMS transformed our maintenance approach, reducing unplanned downtime by 73% 
              and saving us $2.4M annually through predictive insights."
            </blockquote>
            <cite className="text-lg text-gray-600 mb-8 block">
              — Senior Operations Manager, Regional Power Company
            </cite>
            <Link
              to="/case-studies"
              className="inline-flex items-center px-8 py-3 bg-[#233790] text-white font-medium rounded-lg hover:bg-[#1e2d73] transition-colors"
            >
              Read More Case Studies
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Training Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Build In-House Reliability Champions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
              Empower your team with industry leading training programs in reliability engineering, 
              condition monitoring, and asset management best practices.
            </p>
            <Link
              to="/training"
              className="inline-flex items-center px-8 py-3 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-[#F0F4FE] transition-colors"
            >
              View Training Programs <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-20 bg-gradient-to-r from-[#233790] to-[#3BA6EA] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-light mb-6">Ultra Precise. Ultra Performance.</h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-5xl md:text-6xl font-light mb-2">
                <AnimatedCounter end={20} suffix="X" />
              </div>
              <p className="text-xl text-blue-100">ROI Achievement</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <div className="text-5xl md:text-6xl font-light mb-2">
                <AnimatedCounter end={99.98} suffix="%" />
              </div>
              <p className="text-xl text-blue-100">Machine Availability</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="text-5xl md:text-6xl font-light mb-2">
                <AnimatedCounter end={98} suffix="%" />
              </div>
              <p className="text-xl text-blue-100">Downtime Reduction</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="text-5xl md:text-6xl font-light mb-2">
                24×7
              </div>
              <p className="text-xl text-blue-100">Continued Support</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dashboard Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="/images/banner.jpg"
            alt="Analytics Dashboard"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#233790]/80 to-[#3BA6EA]/80 backdrop-blur-sm"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 pt-16"
          >
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Reports & Dashboard
            </h2>
            <p className="text-xl text-blue-100">
              Comprehensive insights at your fingertips with intuitive dashboards and detailed reporting
            </p>
          </motion.div>
          
          <motion.div 
            className="relative w-full h-[500px] bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            whileHover={{ 
              transform: 'translateY(-5px)',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#233790]/10 to-transparent rounded-2xl"></div>
            <div className="relative z-10 h-full flex items-center justify-center">
              <div className="text-center max-w-2xl">
                <h3 className="text-2xl font-semibold text-white mb-6">Key Features</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-100">
                  <li className="flex items-center justify-center space-x-2 p-3 bg-white/10 rounded-lg backdrop-blur-sm hover:bg-white/20 transition-colors">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Real-time asset health monitoring</span>
                  </li>
                  <li className="flex items-center justify-center space-x-2 p-3 bg-white/10 rounded-lg backdrop-blur-sm hover:bg-white/20 transition-colors">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Predictive maintenance alerts</span>
                  </li>
                  <li className="flex items-center justify-center space-x-2 p-3 bg-white/10 rounded-lg backdrop-blur-sm hover:bg-white/20 transition-colors">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Performance trend analysis</span>
                  </li>
                  <li className="flex items-center justify-center space-x-2 p-3 bg-white/10 rounded-lg backdrop-blur-sm hover:bg-white/20 transition-colors">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Cost optimization insights</span>
                  </li>
                  <li className="flex items-center justify-center space-x-2 p-3 bg-white/10 rounded-lg backdrop-blur-sm hover:bg-white/20 transition-colors md:col-span-2">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Mobile-responsive interface</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">Frequently Asked Questions</h2>
          </motion.div>
          
          <div>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-[#F0F4FE] to-[#E2EEFE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Ready to Transform Your Asset Management?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Let's discuss how AAMS can optimize your operations and deliver measurable results 
              for your industrial assets.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-3 bg-[#233790] text-white font-medium rounded-lg hover:bg-[#1e2d73] transition-colors"
            >
              Contact Us Today
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;